import React, { useEffect, useRef, useMemo, FC, useCallback } from "react";
import { EvergisDynamicLayer, useMapLevel } from "@evergis/react";
import { useDispatch } from "react-redux";
import { addLoadingLayer, removeLoadingLayer } from "../../../ducks/loader/loader";

import { usePostgresLayerWithTable } from "../../../hooks/layer/usePostgresLayerWithTable";
import { scaleIsVisible } from "../../../evergis/core/scale";
import { useProjectCrs } from "../../../hooks/project/useProjectCrs";
import { useTempStyleName } from "../../../hooks/style/useTempStyleName";
import { useDefaultScaleRange } from "../../../hooks/style/useDefaultScaleRange";
import { useLayersTs } from "../../../hooks/layer/useLayersTs";
import { useIsDashboard } from "../../../hooks/location/useIsDashboard";
import { useWidgetConfiguration } from "../../Dashboard/hooks/useWidgetConfiguration";
import { useRefreshLayer } from "../../../hooks/layer/useRefreshLayer";

import { WidgetType } from "../../FeatureCard/types";

export const Layer: FC<Omit<PropsOf<typeof EvergisDynamicLayer>, "style">> = ({
  name,
  zIndex,
  isDisplayed,
  opacity,
  condition,
  dataFilterId,
}) => {
  const mapLevel = useMapLevel();
  const { status, layer, loading } = usePostgresLayerWithTable(name);
  const tempStyleName = useTempStyleName(name);
  const initialRender = useRef(true);
  const defaultScaleRange = useDefaultScaleRange();
  const crs = useProjectCrs();
  const dispatch = useDispatch();
  const [layersTs] = useLayersTs();
  const isDashboard = useIsDashboard();
  const config = useWidgetConfiguration(WidgetType.Dashboard);
  const { position: configPosition, resolution: configResolution } = config.currentConfig?.options || {};
  const refreshLayer = useRefreshLayer();

  const onLayerFetch = useCallback(() => {
    dispatch(addLoadingLayer({ name, alias: layer.alias }));
  }, [name, layer.alias]);
  const onLayerFetchEnd = useCallback(() => {
    dispatch(removeLoadingLayer(name));
  }, [name]);

  useEffect(() => {
    initialRender.current = false;
  }, [layer.name]);

  useEffect(() => {
    return onLayerFetchEnd;
  }, [onLayerFetchEnd]);

  useEffect(() => {
    if (loading) {
      onLayerFetch();
    } else {
      onLayerFetchEnd();
    }
  }, [loading, onLayerFetch, onLayerFetchEnd]);

  useEffect(() => {
    if (!isDashboard) {
      return;
    }

    window.setTimeout(() => {
      refreshLayer(layer.name);
    }, 501);
  }, [isDashboard, configPosition?.[0], configPosition?.[1], configResolution]);

  const isVisibleScale = useMemo(
    () => scaleIsVisible(layer.style, mapLevel, defaultScaleRange),
    [defaultScaleRange, layer.style, mapLevel],
  );

  if (status || initialRender.current) {
    return null;
  }

  return (
    <EvergisDynamicLayer
      name={name}
      zIndex={zIndex}
      isDisplayed={isVisibleScale && isDisplayed}
      style={tempStyleName}
      condition={condition || undefined}
      opacity={opacity}
      dataFilterId={dataFilterId}
      crs={crs}
      ts={layersTs[name]}
      onLoadStart={onLayerFetch}
      onLoadEnd={onLayerFetchEnd}
    />
  );
};
